import React, { useState, useEffect, useRef } from 'react';
import theme from '../../../theme/globalTheme';
import { useParams } from 'react-router-dom';
import {
  Card, Button, Steps, InputNumber, Typography, Row, Col, 
  Divider, Alert, message, Space, Tag, Modal, Spin, Result, Input
} from 'antd';
import {
  ShoppingCartOutlined, ClockCircleOutlined, DollarOutlined,
  CheckCircleOutlined, FileTextOutlined, LinkOutlined,
  HomeOutlined, ExclamationCircleOutlined, MailOutlined,
  WarningOutlined, ArrowRightOutlined, EditOutlined
} from '@ant-design/icons';
import { supabase } from '../../../api/supabase';
import { VoxPayService } from '../../../services/voxpayService';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const ClientMenuSelection = () => {
  const { uniqueLink } = useParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [menuCategories, setMenuCategories] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
  const [contractVisible, setContractVisible] = useState(false);
  const [selectionModified, setSelectionModified] = useState(false);
  const [clientSignature, setClientSignature] = useState('');
  const [signatureDate, setSignatureDate] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [showEmailWarning, setShowEmailWarning] = useState(false);
  const [voxpayService] = useState(new VoxPayService());
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Company theme colors
  const theme = {
    primary: '#000000',
    secondary: '#ffffff',
    accent: '#666666',
    lightGrey: '#f5f5f5',
    mediumGrey: '#d9d9d9',
    success: '#52c41a',
    warning: '#faad14',
    error: '#ff4d4f',
    gradient: 'linear-gradient(135deg, #000000 0%, #434343 100%)'
  };

  useEffect(() => {
    if (uniqueLink) {
      fetchReservationData();
      fetchMenuItems();
    }
  }, [uniqueLink]);

  useEffect(() => {
    calculateTotal();
  }, [selectedItems]);

  // Auto-reload when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && uniqueLink) {
        console.log('🔄 Page became visible - reloading reservation data');
        fetchReservationData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [uniqueLink]);

  // ✅ FIXED: Separate queries to avoid relationship ambiguity
  const fetchReservationData = async () => {
    setLoading(true);
    try {
      console.log('🔍 Fetching reservation for unique_link:', uniqueLink);
      
      // Step 1: Get the main reservation
      const { data: reservation, error: reservationError } = await supabase
        .from('lunch_reservations')
        .select('*')
        .eq('unique_link', uniqueLink)
        .single();

      if (reservationError) {
        console.error('❌ Reservation fetch error:', reservationError);
        throw reservationError;
      }
      
      if (!reservation) {
        console.error('❌ No reservation found for unique_link:', uniqueLink);
        message.error('Réservation non trouvée');
        return;
      }

      console.log('✅ Reservation found:', { id: reservation.id, status: reservation.status });

      // Step 2: Get reservation items
      const { data: items, error: itemsError } = await supabase
        .from('lunch_reservation_items')
        .select('*')
        .eq('lunch_reservation_id', reservation.id);

      if (itemsError) {
        console.warn('⚠️ Warning loading items:', itemsError);
      } else {
        console.log('✅ Items loaded:', items?.length || 0);
      }

      // Step 3: Get signed contracts
      const { data: contracts, error: contractsError } = await supabase
        .from('lunch_signed_contracts')
        .select('*')
        .eq('lunch_reservation_id', reservation.id);

      if (contractsError) {
        console.warn('⚠️ Warning loading contracts:', contractsError);
      } else {
        console.log('✅ Contracts loaded:', contracts?.length || 0);
      }

      // Combine data (matching original structure)
      const data = {
        ...reservation,
        lunch_reservation_items: items || [],
        lunch_signed_contracts: contracts || []
      };

      setReservation(data);
      
      // Load existing cart items from database
      if (data.lunch_reservation_items && data.lunch_reservation_items.length > 0) {
        console.log('🛒 Loading existing cart items from database:', data.lunch_reservation_items);
        
        const existingItems = data.lunch_reservation_items.map(item => ({
          id: item.id,
          name: item.item_name,
          price: item.unit_price,
          quantity: item.quantity,
          category: item.item_type,
          isFromDatabase: true
        }));
        
        setSelectedItems(existingItems);
        
        if (data.total_amount) {
          setTotalAmount(data.total_amount);
          console.log('💰 Restored total amount from database:', data.total_amount);
        }
      }
      
      const now = new Date();
      const expirationDate = new Date(data.expires_at);
      setIsExpired(now > expirationDate);

      switch (data.status) {
        case 'initialized':
          setCurrentStep(0);
          break;
        case 'selected':
          setCurrentStep(1);
          break;
        case 'signed':
          setCurrentStep(2);
          break;
        case 'paid':
          setCurrentStep(3);
          break;
        default:
          setCurrentStep(0);
      }

      console.log('✅ Reservation data loaded:', {
        status: data.status,
        totalAmount: data.total_amount,
        itemsCount: data.lunch_reservation_items?.length || 0,
        email: data.client_email
      });

    } catch (error) {
      message.error('Erreur lors du chargement de la réservation');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const { data, error } = await supabase
        .from('lunch_menu_items')
        .select('*')
        .eq('available', true)
        .order('category');

      if (error) throw error;

      const grouped = data.reduce((acc, item) => {
        if (!acc[item.category]) {
          acc[item.category] = [];
        }
        acc[item.category].push(item);
        return acc;
      }, {});

      setMenuCategories(grouped);
    } catch (error) {
      message.error('Erreur lors du chargement des menus');
      console.error('Menu fetch error:', error);
    }
  };

  const calculateTotal = () => {
    const total = selectedItems.reduce((sum, item) => {
      return sum + (item.price * item.quantity);
    }, 0);
    
    console.log('💰 Calculating total:', {
      items: selectedItems.length,
      calculatedTotal: total,
      currentTotal: totalAmount
    });
    
    setTotalAmount(total);
  };

  const addToSelection = (menuItem) => {
    const existingIndex = selectedItems.findIndex(item => 
      item.name === menuItem.name && !item.isFromDatabase
    );
    
    if (existingIndex >= 0) {
      const updated = [...selectedItems];
      updated[existingIndex].quantity += 1;
      setSelectedItems(updated);
    } else {
      setSelectedItems([...selectedItems, {
        id: menuItem.id,
        name: menuItem.name,
        price: menuItem.price,
        quantity: 1,
        category: menuItem.category,
        isFromDatabase: false
      }]);
    }
    
    if (reservation?.status !== 'initialized') {
      setSelectionModified(true);
    }
    
    console.log('➕ Added item to cart:', menuItem.name);
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      setSelectedItems(selectedItems.filter(item => item.id !== itemId));
      console.log('🗑️ Removed item from cart:', itemId);
    } else {
      const updated = selectedItems.map(item => 
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      );
      setSelectedItems(updated);
      console.log('🔄 Updated quantity for item:', itemId, 'new quantity:', newQuantity);
    }
    
    if (reservation?.status !== 'initialized') {
      setSelectionModified(true);
    }
  };

  const saveSelection = async () => {
    if (selectedItems.length === 0) {
      message.error('Veuillez sélectionner au moins un élément');
      return;
    }

    if (reservation.status !== 'initialized') {
      console.log('⚠️ Selection already saved, updating...');
      await updateSelection();
      return;
    }

    setLoading(true);
    try {
      // Delete old items
      const { error: deleteError } = await supabase
        .from('lunch_reservation_items')
        .delete()
        .eq('lunch_reservation_id', reservation.id);

      if (deleteError) {
        console.warn('Warning deleting old items:', deleteError);
      }

      // Insert new items
      const itemsToInsert = selectedItems.map(item => ({
        lunch_reservation_id: reservation.id,
        item_type: item.category,
        item_name: item.name,
        quantity: item.quantity,
        unit_price: item.price,
        total_price: item.price * item.quantity
      }));

      const { error: itemsError } = await supabase
        .from('lunch_reservation_items')
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;

      // Update reservation status
      const { error: updateError } = await supabase
        .from('lunch_reservations')
        .update({
          status: 'selected',
          total_amount: totalAmount
        })
        .eq('id', reservation.id);

      if (updateError) throw updateError;

      message.success('Sélection sauvegardée!');
      setCurrentStep(1);
      setReservation({ ...reservation, status: 'selected', total_amount: totalAmount });
      setSelectionModified(false);

      console.log('✅ Selection saved successfully:', {
        itemsCount: itemsToInsert.length,
        totalAmount: totalAmount
      });

    } catch (error) {
      message.error('Erreur lors de la sauvegarde');
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSelection = async () => {
    setLoading(true);
    try {
      // Delete existing items
      const { error: deleteError } = await supabase
        .from('lunch_reservation_items')
        .delete()
        .eq('lunch_reservation_id', reservation.id);

      if (deleteError) throw deleteError;

      // Insert updated items
      const itemsToInsert = selectedItems.map(item => ({
        lunch_reservation_id: reservation.id,
        item_type: item.category,
        item_name: item.name,
        quantity: item.quantity,
        unit_price: item.price,
        total_price: item.price * item.quantity
      }));

      const { error: itemsError } = await supabase
        .from('lunch_reservation_items')
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;

      // Update total amount
      const { error: updateError } = await supabase
        .from('lunch_reservations')
        .update({ total_amount: totalAmount })
        .eq('id', reservation.id);

      if (updateError) throw updateError;

      message.success('Sélection mise à jour!');
      setReservation({ ...reservation, total_amount: totalAmount });
      setSelectionModified(false);

      console.log('✅ Selection updated successfully:', {
        itemsCount: itemsToInsert.length,
        totalAmount: totalAmount
      });

    } catch (error) {
      message.error('Erreur lors de la mise à jour');
      console.error('Update error:', error);
    } finally {
      setLoading(false);
    }
  };

  const proceedToNextStep = () => {
    if (selectionModified) {
      updateSelection().then(() => {
        if (reservation.status === 'selected') {
          setCurrentStep(1);
        } else if (reservation.status === 'signed') {
          setCurrentStep(2);
        }
      });
    } else {
      if (reservation.status === 'selected') {
        setCurrentStep(1);
      } else if (reservation.status === 'signed') {
        setCurrentStep(2);
      }
    }
  };

  // ✅ SIGNATURE ÉLECTRONIQUE
  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    setClientSignature(signatureData);
    setSignatureDate(new Date().toISOString());
    console.log('✅ Signature saved');
  };

  // ✅ SIGNER LE CONTRAT AVEC SIGNATURE
  const signContract = async () => {
    if (!clientSignature) {
      message.error('Veuillez signer avant de valider');
      return;
    }

    setLoading(true);
    try {
      const contractContent = generateContractContent();
      
      const { error } = await supabase
        .from('lunch_signed_contracts')
        .insert([{
          lunch_reservation_id: reservation.id,
          contract_content: contractContent,
          signature_data: clientSignature,
          signature_date: signatureDate,
          client_ip: 'client_ip'
        }]);

      if (error) throw error;

      const { error: updateError } = await supabase
        .from('lunch_reservations')
        .update({ status: 'signed' })
        .eq('id', reservation.id);

      if (updateError) throw updateError;

      message.success('Contrat signé avec succès!');
      setCurrentStep(2);
      setReservation({ ...reservation, status: 'signed' });
      setContractVisible(false);

      console.log('✅ Contract signed, status updated to "signed"');

    } catch (error) {
      message.error('Erreur lors de la signature');
      console.error('Sign error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ VOXPAY PAYMENT INTEGRATION
  const processVoxPayPayment = async () => {
    if (totalAmount <= 0) {
      message.error('Montant invalide pour le paiement');
      return;
    }

    setPaymentLoading(true);
    try {
      console.log('💳 Processing VoxPay payment:', {
        amount: totalAmount,
        client: reservation.client_name,
        email: reservation.client_email
      });

      const paymentData = {
        amount: totalAmount,
        customer: {
          name: reservation.client_name,
          email: reservation.client_email,
          phone: reservation.client_phone || ''
        },
        order: {
          reference: `LUNCH-${reservation.id}`,
          description: `Lunch Master Ski Pilou - ${dayjs(reservation.charter_date).format('DD/MM/YYYY')}`
        },
        callback_url: `${window.location.origin}/lunch-payment-callback/${reservation.unique_link}`
      };

      const result = await voxpayService.createPaymentLink(paymentData);
      
      if (result.success) {
        message.success('Redirection vers VoxPay...');
        window.location.href = result.payment_url;
      } else {
        throw new Error(result.error);
      }

    } catch (error) {
      message.error('Erreur lors de la création du paiement VoxPay');
      console.error('VoxPay error:', error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const testVoxPayConnection = async () => {
    setPaymentLoading(true);
    try {
      const result = await voxpayService.testConnection();
      if (result.success) {
        message.success('✅ VoxPay connecté');
      } else {
        message.error('❌ VoxPay déconnecté: ' + result.error);
      }
    } catch (error) {
      message.error('❌ Erreur test VoxPay');
      console.error('VoxPay test error:', error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const showVoxPayConfig = () => {
    Modal.info({
      title: '🔧 Configuration VoxPay',
      content: (
        <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          <p><strong>API Key:</strong> {process.env.REACT_APP_VOXPAY_API_KEY ? '✅ Configuré' : '❌ Manquant'}</p>
          <p><strong>Campaign ID:</strong> {process.env.REACT_APP_VOXPAY_CAMPAIGN_ID ? '✅ Configuré' : '❌ Manquant'}</p>
          <p><strong>Environment:</strong> {process.env.REACT_APP_VOXPAY_ENV || 'production'}</p>
          <p><strong>Base URL:</strong> {process.env.REACT_APP_VOXPAY_BASE_URL || 'https://api.voxpay.fr'}</p>
        </div>
      )
    });
  };

  const simulateSuccessfulPayment = async () => {
    try {
      console.log('🎭 Simulating successful payment...');
      
      const { error } = await supabase
        .from('lunch_reservations')
        .update({ 
          status: 'paid',
          payment_date: new Date().toISOString(),
          payment_method: 'simulation'
        })
        .eq('id', reservation.id);

      if (error) throw error;

      await sendNotificationToMasterSki();
      
      message.success('🎉 Paiement simulé avec succès!');
      setCurrentStep(3);
      setReservation({ ...reservation, status: 'paid' });

    } catch (error) {
      message.error('Erreur lors de la simulation');
      console.error('Simulation error:', error);
    }
  };

  // ✅ ENVOYER NOTIFICATION EMAIL QUAND SIGNÉ ET PAYÉ
  const sendNotificationToMasterSki = async () => {
    try {
      console.log('📧 Sending notification to Master Ski Pilou (Signed + Paid)');
      
      const emailData = {
        to: 'masterskipilou@example.com', // Remplacer par le vrai email
        subject: `🎉 Réservation Complète - ${reservation.client_name} - ${dayjs(reservation.charter_date).format('DD/MM/YYYY')}`,
        message: `
🏝️ RÉSERVATION LUNCH COMPLÈTE - MASTER SKI PILOU

✅ CONTRAT SIGNÉ ET PAIEMENT EFFECTUÉ

📋 DÉTAILS CLIENT:
• Nom: ${reservation.client_name}
• Email: ${reservation.client_email}
• Téléphone: ${reservation.client_phone || 'Non renseigné'}
• Date charter: ${dayjs(reservation.charter_date).format('DD/MM/YYYY')}
• Bateau: ${reservation.boat_name}
• Type sortie: ${reservation.trip_type}

🛒 ARTICLES COMMANDÉS:
${selectedItems.map(item => `• ${item.name}: ${item.quantity} x ${item.price}€ = ${item.price * item.quantity}€`).join('\n')}

💰 MONTANT TOTAL: ${totalAmount}€

✍️ SIGNATURE:
• Contrat signé électroniquement le: ${dayjs(signatureDate).format('DD/MM/YYYY HH:mm')}
• Paiement effectué le: ${dayjs().format('DD/MM/YYYY HH:mm')}

🔗 LIEN RÉSERVATION:
${window.location.origin}/lunch-reservation/${reservation.unique_link}

📧 Le client attend maintenant votre validation dans le CRM pour recevoir son email de confirmation.

---
Système Master Ski Pilou
${dayjs().format('DD/MM/YYYY HH:mm')}
        `,
        reservation_id: reservation.id,
        client_email: reservation.client_email,
        total_amount: totalAmount,
        status: 'signed_and_paid'
      };

      // Webhook vers Make (Integromat) ou EmailJS
      const webhookUrl = process.env.REACT_APP_MAKE_NOTIFICATION_WEBHOOK;
      if (webhookUrl) {
        await fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(emailData)
        });
        console.log('✅ Notification sent to Master Ski Pilou (Signed + Paid)');
      } else {
        console.warn('⚠️ Webhook URL not configured - notification not sent');
      }

    } catch (error) {
      console.error('❌ Error sending notification:', error);
    }
  };

  // ✅ GÉNÉRATION DU CONTRAT PDF
  const generateContractContent = () => {
    if (!reservation) return '';
    
    return `
CONTRAT DE PRESTATION DE SERVICE - LUNCH MASTER SKI PILOU

Client: ${reservation.client_name}
Email: ${reservation.client_email}
Téléphone: ${reservation.client_phone || 'Non renseigné'}
Date du charter: ${dayjs(reservation.charter_date).format('DD/MM/YYYY')}
Bateau: ${reservation.boat_name}
Type de sortie: ${reservation.trip_type}

ARTICLES COMMANDÉS:
${selectedItems.map(item => 
  `- ${item.name}: ${item.quantity} x ${item.price}€ = ${item.price * item.quantity}€`
).join('\n')}

MONTANT TOTAL: ${totalAmount}€

CONDITIONS:
- Commande à confirmer 24h avant pour les alcools
- Annulation possible jusqu'à 48h avant
- Paiement sécurisé en ligne

Date de signature électronique: ${dayjs(signatureDate).format('DD/MM/YYYY HH:mm')}
Adresse IP: ${window.location.hostname}

MASTER SKI PILOU
Rue Jeanne d'Arc – GUSTAVIA - 97133 Saint-Barthélemy
Tél. : 05 90 27 91 79
Siret n° 48013153100018
    `.trim();
  };

  const categoryTitles = {
    menu: 'Menus de Base',
    alcohol: 'Boissons Alcoolisées',
    premium_add: 'Suppléments Premium',
    sharing_plate: 'Plateaux à Partager'
  };

  const categoryIcons = {
    menu: '🍽️',
    alcohol: '🍷',
    premium_add: '⭐',
    sharing_plate: '🧀'
  };

  if (loading && !reservation) {
    return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark,  
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: theme.gradient
      }}>
        <Card style={{ textAlign: 'center', maxWidth: 400, border: `2px solid ${theme.mediumGrey}` }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Chargement de votre réservation...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (!reservation || isExpired || reservation.status === 'blocked') {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: theme.gradient
      }}>
        <Card style={{ maxWidth: 500, border: `2px solid ${theme.mediumGrey}` }}>
          <Result
            status={!reservation ? "404" : "warning"}
            title={!reservation ? "Réservation non trouvée" : isExpired ? "Lien expiré" : "Réservation bloquée"}
            subTitle={!reservation ? "Le lien de réservation est invalide." : isExpired ? "Ce lien a expiré (48h avant la sortie)." : "Cette réservation a été temporairement bloquée."}
            extra={
              <Button 
                type="primary" 
                icon={<HomeOutlined />}
                style={{ 
                  backgroundColor: theme.primary, 
                  borderColor: theme.primary,
                  color: theme.secondary 
                }}
              >
                Contacter Master Ski Pilou
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: theme.gradient,
      padding: '24px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Company Header */}
        <Card style={{ 
          marginBottom: '24px', 
          textAlign: 'center',
          border: `2px solid ${theme.mediumGrey}`,
          background: theme.secondary
        }}>
          <Title level={2} style={{ margin: '0 0 8px 0', color: theme.primary }}>
            🏝️ Master Ski Pilou
          </Title>
          <Title level={3} style={{ margin: '0 0 16px 0', color: theme.accent }}>
            Réservation Lunch
          </Title>
          <Text type="secondary" style={{ fontSize: '16px', color: theme.accent }}>
            Bonjour <strong style={{ color: theme.primary }}>{reservation.client_name}</strong>, 
            sélectionnez votre menu pour le <strong style={{ color: theme.primary }}>{dayjs(reservation.charter_date).format('DD/MM/YYYY')}</strong>
          </Text>
        </Card>

        {/* Steps */}
        <Card style={{ 
          marginBottom: '24px',
          border: `1px solid ${theme.mediumGrey}`,
          background: theme.secondary
        }}>
          <Steps current={currentStep} size="small">
            <Step
              title="Sélection"
              icon={<ShoppingCartOutlined style={{ color: currentStep >= 0 ? theme.primary : theme.accent }} />}
              description="Choisissez vos plats"
            />
            <Step
              title="Signature"
              icon={<EditOutlined style={{ color: currentStep >= 1 ? theme.primary : theme.accent }} />}
              description="Signature électronique"
            />
            <Step
              title="Paiement"
              icon={<DollarOutlined style={{ color: currentStep >= 2 ? theme.primary : theme.accent }} />}
              description="Paiement VoxPay"
            />
            <Step
              title="Confirmation"
              icon={<CheckCircleOutlined style={{ color: currentStep >= 3 ? theme.success : theme.accent }} />}
              description="Réservation confirmée"
            />
          </Steps>
        </Card>

        {/* Step 1: Selection */}
        {currentStep === 0 && (
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={16}>
              {Object.entries(menuCategories).map(([category, items]) => (
                <Card 
                  key={category}
                  title={
                    <Space>
                      <span style={{ fontSize: '20px' }}>{categoryIcons[category]}</span>
                      <span style={{ color: theme.primary }}>{categoryTitles[category]}</span>
                    </Space>
                  }
                  style={{ 
                    marginBottom: '24px',
                    border: `1px solid ${theme.mediumGrey}`,
                    background: theme.secondary
                  }}
                  size="small"
                >
                  <Row gutter={[16, 16]}>
                    {items.map(item => (
                      <Col xs={24} sm={12} lg={8} key={item.id}>
                        <Card 
                          size="small"
                          hoverable
                          style={{ 
                            height: '100%',
                            border: `1px solid ${theme.lightGrey}`,
                            background: theme.secondary
                          }}
                          bodyStyle={{ padding: '12px' }}
                          actions={[
                            <Button 
                              type="primary" 
                              size="small"
                              onClick={() => addToSelection(item)}
                              disabled={item.requires_advance_notice && 
                                dayjs(reservation.charter_date).diff(dayjs(), 'hour') < 24}
                              style={{
                                backgroundColor: theme.primary,
                                borderColor: theme.primary,
                                color: theme.secondary
                              }}
                            >
                              Ajouter
                            </Button>
                          ]}
                        >
                          <div>
                            <Text strong style={{ fontSize: '14px', color: theme.primary }}>{item.name}</Text>
                            {item.description && (
                              <div style={{ marginTop: '4px' }}>
                                <Text type="secondary" style={{ fontSize: '12px', color: theme.accent }}>
                                  {item.description}
                                </Text>
                              </div>
                            )}
                            <div style={{ marginTop: '8px' }}>
                              <Tag color="default" style={{ 
                                margin: 0, 
                                backgroundColor: theme.lightGrey,
                                border: `1px solid ${theme.mediumGrey}`,
                                color: theme.primary
                              }}>{item.price}€</Tag>
                              {item.requires_advance_notice && (
                                <Tag color="orange" icon={<ClockCircleOutlined />} style={{ marginLeft: '4px' }}>
                                  24h
                                </Tag>
                              )}
                            </div>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card>
              ))}
            </Col>

            <Col xs={24} lg={8}>
              <Card 
                title="🛒 Votre Sélection" 
                style={{ 
                  position: 'sticky', 
                  top: '24px',
                  border: `2px solid ${theme.mediumGrey}`,
                  background: theme.secondary
                }}
              >
                {selectedItems.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px' }}>
                    <Text type="secondary" style={{ color: theme.accent }}>Aucun élément sélectionné</Text>
                  </div>
                ) : (
                  <>
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {selectedItems.map(item => (
                        <div key={`${item.id}-${item.isFromDatabase}`} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '12px',
                          padding: '8px',
                          background: item.isFromDatabase ? theme.lightGrey : '#f9f9f9',
                          borderRadius: '6px',
                          border: item.isFromDatabase ? `1px solid ${theme.mediumGrey}` : `1px solid ${theme.lightGrey}`
                        }}>
                          <div style={{ flex: 1 }}>
                            <Text strong style={{ fontSize: '13px', color: theme.primary }}>{item.name}</Text>
                            <div>
                              <Text type="secondary" style={{ fontSize: '12px', color: theme.accent }}>{item.price}€/unité</Text>
                              {item.isFromDatabase && (
                                <Tag size="small" color="default" style={{ 
                                  marginLeft: '4px',
                                  backgroundColor: theme.accent,
                                  color: theme.secondary,
                                  border: 'none'
                                }}>Sauvé</Tag>
                              )}
                            </div>
                          </div>
                          <InputNumber
                            min={0}
                            value={item.quantity}
                            onChange={(value) => updateQuantity(item.id, value)}
                            size="small"
                            style={{ width: '60px' }}
                          />
                        </div>
                      ))}
                    </div>
                    
                    <Divider style={{ margin: '16px 0', borderColor: theme.mediumGrey }} />
                    
                    <div style={{ textAlign: 'center' }}>
                      <Title level={4} style={{ margin: '0 0 16px 0', color: theme.success }}>
                        Total: {totalAmount}€
                      </Title>
                      
                      {reservation.status === 'initialized' ? (
                        <Button 
                          type="primary" 
                          size="large"
                          block
                          onClick={saveSelection}
                          loading={loading}
                          disabled={selectedItems.length === 0}
                          style={{
                            backgroundColor: theme.primary,
                            borderColor: theme.primary,
                            color: theme.secondary
                          }}
                        >
                          ✅ Valider la Sélection
                        </Button>
                      ) : (
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {selectionModified ? (
                            <Space direction="vertical" style={{ width: '100%' }}>
                              <Button 
                                type="primary" 
                                size="large"
                                block
                                onClick={updateSelection}
                                loading={loading}
                                disabled={selectedItems.length === 0}
                                style={{
                                  backgroundColor: theme.warning,
                                  borderColor: theme.warning,
                                  color: theme.secondary
                                }}
                              >
                                💾 Sauvegarder les Modifications
                              </Button>
                              <Button 
                                type="default" 
                                size="large"
                                block
                                onClick={proceedToNextStep}
                                disabled={selectionModified || selectedItems.length === 0}
                                icon={<ArrowRightOutlined />}
                                style={{
                                  backgroundColor: theme.primary,
                                  borderColor: theme.primary,
                                  color: theme.secondary
                                }}
                              >
                                Continuer vers la Signature
                              </Button>
                            </Space>
                          ) : (
                            <Button 
                              type="primary" 
                              size="large"
                              block
                              onClick={proceedToNextStep}
                              disabled={selectedItems.length === 0}
                              icon={<ArrowRightOutlined />}
                              style={{
                                backgroundColor: theme.primary,
                                borderColor: theme.primary,
                                color: theme.secondary
                              }}
                            >
                              Continuer vers {reservation.status === 'selected' ? 'la Signature' : 'le Paiement'}
                            </Button>
                          )}
                          <Text type="secondary" style={{ fontSize: '12px', color: theme.accent }}>
                            {selectionModified ? 'Modifications non sauvegardées' : 'Sélection sauvegardée'}
                          </Text>
                        </Space>
                      )}
                    </div>
                  </>
                )}
              </Card>
            </Col>
          </Row>
        )}

        {/* Step 2: Signature */}
        {currentStep === 1 && (
          <Card style={{ 
            textAlign: 'center',
            border: `2px solid ${theme.mediumGrey}`,
            background: theme.secondary
          }}>
            <CheckCircleOutlined style={{ fontSize: '64px', color: theme.success, marginBottom: '16px' }} />
            <Title level={3} style={{ color: theme.primary }}>Sélection Confirmée!</Title>
            <Paragraph style={{ fontSize: '16px', color: theme.accent }}>
              Votre sélection a été enregistrée pour un montant de <strong style={{ color: theme.primary }}>{totalAmount}€</strong>
            </Paragraph>
            
            <Alert
              message="📝 Signature Électronique Requise"
              description="Veuillez signer électroniquement votre contrat de prestation pour valider votre commande."
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />
            
            <Space direction="vertical" size="large">
              <Button 
                type="primary" 
                size="large" 
                onClick={() => {
                  setContractVisible(true);
                  setTimeout(initializeCanvas, 100);
                }}
                icon={<EditOutlined />}
                style={{
                  backgroundColor: theme.primary,
                  borderColor: theme.primary,
                  color: theme.secondary
                }}
              >
                📝 Voir et Signer le Contrat
              </Button>
              
              <Button
                type="default"
                onClick={() => setCurrentStep(0)}
                icon={<ShoppingCartOutlined />}
                style={{
                  backgroundColor: theme.lightGrey,
                  borderColor: theme.mediumGrey,
                  color: theme.primary
                }}
              >
                🔄 Modifier ma Sélection
              </Button>
            </Space>
          </Card>
        )}

        {/* Step 3: VoxPay Payment */}
        {currentStep === 2 && (
          <Row gutter={24}>
            <Col xs={24} lg={16}>
              <Card style={{ 
                textAlign: 'center',
                border: `2px solid ${theme.mediumGrey}`,
                background: theme.secondary
              }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>💳</div>
                <Title level={3} style={{ color: theme.primary }}>Paiement Sécurisé VoxPay</Title>
                <Paragraph style={{ fontSize: '16px', color: theme.accent }}>
                  Vous allez être redirigé vers VoxPay pour régler <strong style={{ color: theme.primary }}>{totalAmount}€</strong> en toute sécurité
                </Paragraph>
                
                {totalAmount === 0 && (
                  <Alert
                    message="❌ Montant de 0€ détecté"
                    description="Le montant total est de 0€. Veuillez retourner à l'étape 1 pour sélectionner vos articles."
                    type="error"
                    showIcon
                    style={{ marginBottom: '24px' }}
                    action={
                      <Button size="small" onClick={() => setCurrentStep(0)}>
                        🔄 Retourner à la Sélection
                      </Button>
                    }
                  />
                )}
                
                <Alert
                  message="💳 VoxPay - Paiement Sécurisé"
                  description="Plateforme de paiement française certifiée PCI-DSS. Paiement par email/web sans appel téléphonique."
                  type="info"
                  showIcon
                  style={{ marginBottom: '24px' }}
                />

                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <Button 
                    type="primary" 
                    size="large" 
                    onClick={processVoxPayPayment}
                    loading={paymentLoading}
                    icon={<LinkOutlined />}
                    style={{ 
                      minWidth: '300px', 
                      backgroundColor: theme.primary,
                      borderColor: theme.primary,
                      color: theme.secondary
                    }}
                    disabled={totalAmount === 0}
                  >
                    💳 Payer avec VoxPay - {totalAmount}€
                  </Button>
                  
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary" style={{ color: theme.accent }}>Tests et Diagnostic</Text>
                  </div>
                  
                  <Space>
                    <Button 
                      type="default" 
                      size="large" 
                      onClick={testVoxPayConnection}
                      loading={paymentLoading}
                      style={{
                        backgroundColor: theme.lightGrey,
                        borderColor: theme.mediumGrey,
                        color: theme.primary
                      }}
                    >
                      🧪 Test VoxPay
                    </Button>
                    
                    <Button 
                      type="default" 
                      size="small" 
                      onClick={showVoxPayConfig}
                      style={{
                        backgroundColor: theme.lightGrey,
                        borderColor: theme.mediumGrey,
                        color: theme.primary
                      }}
                    >
                      🔍 Config
                    </Button>
                  </Space>
                  
                  <Button 
                    type="default" 
                    size="large" 
                    onClick={() => {
                      setPaymentLoading(true);
                      simulateSuccessfulPayment().finally(() => setPaymentLoading(false));
                    }}
                    loading={paymentLoading}
                    style={{ 
                      minWidth: '300px',
                      backgroundColor: theme.accent,
                      borderColor: theme.accent,
                      color: theme.secondary
                    }}
                    disabled={totalAmount === 0}
                  >
                    🎭 Simuler Paiement (Dev)
                  </Button>
                  
                  <Button
                    type="default"
                    onClick={() => setCurrentStep(0)}
                    icon={<ShoppingCartOutlined />}
                    style={{
                      backgroundColor: theme.lightGrey,
                      borderColor: theme.mediumGrey,
                      color: theme.primary
                    }}
                  >
                    🔄 Modifier ma Sélection
                  </Button>
                </Space>

                <div style={{ marginTop: '16px' }}>
                  <Text type="secondary" style={{ fontSize: '14px', color: theme.accent }}>
                    🔒 Connexion sécurisée SSL - VoxPay France
                  </Text>
                </div>
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card 
                title="📋 Récapitulatif VoxPay" 
                size="small"
                style={{
                  border: `1px solid ${theme.mediumGrey}`,
                  background: theme.secondary
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div><strong style={{ color: theme.primary }}>Client:</strong> <span style={{ color: theme.accent }}>{reservation.client_name}</span></div>
                  <div><strong style={{ color: theme.primary }}>Email:</strong> <span style={{ color: theme.accent }}>{reservation.client_email}</span></div>
                  {reservation.client_phone && (
                    <div><strong style={{ color: theme.primary }}>Téléphone:</strong> <span style={{ color: theme.accent }}>{reservation.client_phone}</span></div>
                  )}
                  <div><strong style={{ color: theme.primary }}>Date:</strong> <span style={{ color: theme.accent }}>{dayjs(reservation.charter_date).format('DD/MM/YYYY')}</span></div>
                  <div><strong style={{ color: theme.primary }}>Bateau:</strong> <span style={{ color: theme.accent }}>{reservation.boat_name}</span></div>
                  <Divider style={{ margin: '12px 0', borderColor: theme.mediumGrey }} />
                  {selectedItems.map(item => (
                    <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: theme.accent }}>{item.name} x{item.quantity}</span>
                      <span style={{ color: theme.primary, fontWeight: 'bold' }}>{item.price * item.quantity}€</span>
                    </div>
                  ))}
                  <Divider style={{ margin: '12px 0', borderColor: theme.mediumGrey }} />
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    fontSize: '16px', 
                    fontWeight: 'bold',
                    color: totalAmount === 0 ? theme.error : theme.primary
                  }}>
                    <span>Total VoxPay:</span>
                    <span>{totalAmount}€</span>
                  </div>
                  
                  <Alert
                    message={totalAmount > 0 ? "✅ VoxPay Ready" : "❌ Montant Invalide"}
                    description={totalAmount > 0 ? "Paiement par email/web sécurisé" : "Montant de 0€ - retournez à la sélection"}
                    type={totalAmount > 0 ? "success" : "error"}
                    size="small"
                    showIcon
                  />
                  
                  <div style={{ fontSize: '11px', color: theme.accent, marginTop: '8px' }}>
                    <div>🔑 API Key: {process.env.REACT_APP_VOXPAY_API_KEY ? '✅' : '❌'}</div>
                    <div>🎯 Campaign: {process.env.REACT_APP_VOXPAY_CAMPAIGN_ID ? '✅' : '❌'}</div>
                    <div>🌍 Env: {process.env.REACT_APP_VOXPAY_ENV || 'production'}</div>
                    <div>💰 Amount: {totalAmount}€</div>
                    <div>📧 Email: {reservation.client_email?.split('@')[1] || 'N/A'}</div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        )}

        {/* Step 4: Confirmation */}
        {currentStep === 3 && (
          <Card style={{
            border: `2px solid ${theme.mediumGrey}`,
            background: theme.secondary
          }}>
            <Result
              status="success"
              title={<span style={{ color: theme.primary }}>Contrat Signé et Paiement Effectué! 🎉</span>}
              subTitle={
                <div>
                  <p style={{ fontSize: '16px', marginBottom: '16px', color: theme.accent }}>
                    Merci <strong style={{ color: theme.primary }}>{reservation.client_name}</strong>! 
                  </p>
                  <Alert
                    message="✅ Étapes complétées"
                    description={
                      <div>
                        <p>✍️ Contrat signé électroniquement</p>
                        <p>💳 Paiement effectué via VoxPay</p>
                        <p>📧 Master Ski Pilou a été notifié</p>
                        <p>⏳ Vous recevrez un email de confirmation après validation</p>
                      </div>
                    }
                    type="success"
                    showIcon
                  />
                </div>
              }
              extra={[
                <Button 
                  type="primary" 
                  key="home" 
                  onClick={() => window.location.href = process.env.REACT_APP_BASE_URL || '/'}
                  style={{
                    backgroundColor: theme.primary,
                    borderColor: theme.primary,
                    color: theme.secondary
                  }}
                >
                  🏠 Retour à l'accueil
                </Button>
              ]}
            />
          </Card>
        )}

        {/* Modal Contrat avec Signature */}
        <Modal
          title={<span style={{ color: theme.primary }}>📋 Contrat de Prestation Master Ski Pilou</span>}
          open={contractVisible}
          onCancel={() => setContractVisible(false)}
          width={900}
          footer={null}
        >
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Contrat */}
            <div style={{ 
              whiteSpace: 'pre-line', 
              padding: '16px', 
              background: theme.lightGrey, 
              borderRadius: '6px',
              fontFamily: 'Arial',
              fontSize: '13px',
              lineHeight: '1.6',
              color: theme.primary,
              border: `1px solid ${theme.mediumGrey}`,
              marginBottom: '24px'
            }}>
              {generateContractContent()}
            </div>

            {/* Zone de signature */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Title level={4} style={{ color: theme.primary }}>Signature Électronique</Title>
              <div style={{ 
                border: `2px dashed ${theme.mediumGrey}`, 
                borderRadius: '6px', 
                padding: '16px',
                backgroundColor: theme.secondary,
                marginBottom: '16px'
              }}>
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={200}
                  style={{
                    border: `1px solid ${theme.lightGrey}`,
                    borderRadius: '4px',
                    cursor: 'crosshair',
                    backgroundColor: theme.colors.glassMedium
                  }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                />
                <div style={{ marginTop: '12px' }}>
                  <Space>
                    <Button size="small" onClick={clearSignature}>
                      🗑️ Effacer
                    </Button>
                    <Button type="primary" size="small" onClick={saveSignature}>
                      💾 Sauvegarder Signature
                    </Button>
                  </Space>
                </div>
                {clientSignature && (
                  <div style={{ marginTop: '12px' }}>
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                      Signature enregistrée ✅
                    </Tag>
                  </div>
                )}
              </div>
            </div>

            {/* Boutons d'action */}
            <div style={{ textAlign: 'center', borderTop: `1px solid ${theme.lightGrey}`, paddingTop: '16px' }}>
              <Space>
                <Button 
                  onClick={() => setContractVisible(false)}
                  style={{
                    backgroundColor: theme.lightGrey,
                    borderColor: theme.mediumGrey,
                    color: theme.primary
                  }}
                >
                  Annuler
                </Button>
                <Button 
                  type="primary" 
                  onClick={signContract}
                  loading={loading}
                  disabled={!clientSignature}
                  style={{
                    backgroundColor: theme.primary,
                    borderColor: theme.primary,
                    color: theme.secondary
                  }}
                >
                  ✍️ Valider et Signer le Contrat
                </Button>
              </Space>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ClientMenuSelection;